export const entities = ['Party', 'Person', 'Address', 'Organization'];

export const mockData = {
  Party: {
    attributes: [
      {
        name: 'PartyID',
        type: 'String/UUID',
        description:
          'Unique identifier for the party. This is the primary key for the Party entity (e.g. a UUID or system-generated ID) and corresponds to the customer’s unique ID on screen (CustomerId). It may also be known as a Party Number or global customer ID.',
        isPK: true,
        isRequired: true,
        isMapped: true,
      },
      {
        name: 'PartyType',
        type: 'Person/Organisation',
        description:
          'Type of party (Person or Organization). Indicates whether the party is an individual or a company. For example, values might be “Person” or “Organization”. This helps route the record to the proper sub-entity.',
        isPK: false,
        isRequired: true,
        isMapped: true,
      },
      {
        name: 'Status',
        type: 'String',
        description: 'Customer status flag. Indicates if the customer is active, inactive, prospect, etc.',
        isPK: false,
        isRequired: false,
        isMapped: true,
      },
      {
        name: 'SourceSystem',
        type: 'Date/Time',
        description:
          'Source system identifier. Optionally record which source or system the master record originated from or is highest priority (e.g. CRM, ERP). Useful in a multi-source MDM environment for tracking lineage.',
        isPK: false,
        isRequired: false,
        isMapped: true,
      },
      {
        name: 'CreatedDate',
        type: 'Date/Time',
        description: 'Timestamps for auditing when the party record was created or last updated.',
        isPK: false,
        isRequired: true,
        isMapped: true,
      },
      // {
      //   name: 'UpdatedDate',
      //   type: 'DateTime',
      //   description: 'Timestamp when the party record was last updated.',
      //   isPK: false,
      //   isRequired: true,
      //   isMapped: true,
      // },
    ],

    relationships: [
      {
        from: 'Party',
        relationship: 'hasOne',
        to: 'Person',
        description: 'A Party of type "Person" is associated with one Person record.',
      },
      {
        from: 'Party',
        relationship: 'hasOne',
        to: 'Organization',
        description: 'A Party of type "Organization" is associated with one Organization record.',
      },
      {
        from: 'Party',
        relationship: 'hasMany',
        to: 'Address',
        description: 'A Party can have multiple addresses linked through PartyID.',
      },
    ],

    survivorship: [
      {
        id: 1,
        attribute: 'Status',
        strategy: 'MostRecent',
        sourcePriority: 'CRM > ERP > External',
        fallback: 'Latest Non-null Value',
      },
      {
        id: 2,
        attribute: 'SourceSystem',
        strategy: 'HighestPrioritySystem',
        sourcePriority: 'CRM > ERP > HR',
        fallback: 'DefaultSource',
      },
      {
        id: 3,
        attribute: 'UpdatedDate',
        strategy: 'LatestTimestamp',
        sourcePriority: 'All',
        fallback: 'MostRecentDate',
      },
    ],

    cleanse: [
      {
        id: 1,
        ruleName: 'Standardize Status Values',
        appliesTo: 'Status',
        providerType: 'Built-in',
        order: 1,
        status: 'Active',
      },
      {
        id: 2,
        ruleName: 'Normalize Source System Names',
        appliesTo: 'SourceSystem',
        providerType: 'Built-in',
        order: 2,
        status: 'Active',
      },
      {
        id: 3,
        ruleName: 'Validate Created Date Format',
        appliesTo: 'CreatedDate',
        providerType: 'Built-in (Date Validator)',
        order: 3,
        status: 'Active',
      },
      {
        id: 4,
        ruleName: 'Validate Updated Date Format',
        appliesTo: 'UpdatedDate',
        providerType: 'Built-in (Date Validator)',
        order: 4,
        status: 'Active',
      },
    ],

    matchRules: [
      {
        label: 'Exact(PartyID)',
        name: 'PartyMatchRule1',
        type: 'Potential Match',
        scope: 'All',
      },
      {
        label: 'CrossEntity(PartyType + SubEntity)',
        name: 'PartyMatchRule2',
        type: 'Potential Match',
        scope: 'All',
      },
    ],
  },
  Organization: {
    attributes: [
      {
        name: 'OrganizationID',
        type: 'String',
        description:
          'Unique identifier for the organization. Like PersonID, this will often correspond 1-to-1 with a PartyID (for an organization-type Party).',
        isPK: true,
        isRequired: true,
        isMapped: true,
      },
      {
        name: 'OrganizationName',
        type: 'String',
        description:
          'Name of the organization (legal name or primary name). This is the company’s name as it should appear on records (e.g., “Acme Corporation”).',
        isPK: false,
        isRequired: true,
        isMapped: true,
      },
      {
        name: 'AlternateName',
        type: 'String',
        description:
          'Alternate or trading name. If the business uses a DBA (“Doing Business As”) or short name different from the legal name, it can be stored here.',
        isPK: false,
        isRequired: false,
        isMapped: false,
      },
      {
        name: 'OrganizationType',
        type: 'String',
        description:
          'Type or classification of organization. This could be a broad category like “Company”, “Government”, “Non-Profit”, or an industry-based classification. It might also capture whether it’s a Customer, Vendor, or Partner (depending on context).',
        isPK: false,
        isRequired: false,
        isMapped: true,
      },
      {
        name: 'TaxID',
        type: 'String',
        description:
          'Tax or business identification number. A standard attribute for organizations representing tax identifiers or registration numbers (e.g., VAT Number, EIN, or Business Registration Number).',
        isPK: false,
        isRequired: false,
        isMapped: true,
      },
      {
        name: 'Industry',
        type: 'String',
        description:
          'Industry or sector of the organization (optional). Examples include “Manufacturing”, “Finance”, or “Healthcare”.',
        isPK: false,
        isRequired: false,
        isMapped: true,
      },
      {
        name: 'AnnualRevenue',
        type: 'Number',
        description:
          'Annual revenue of the company. (Optional; commonly used for business profiling and segmentation.)',
        isPK: false,
        isRequired: false,
        isMapped: true,
      },
      {
        name: 'EmployeeCount',
        type: 'Number',
        description:
          'Number of employees in the organization. (Optional; another profiling attribute useful for analytics and segmentation.)',
        isPK: false,
        isRequired: false,
        isMapped: true,
      },
      {
        name: 'WebsiteURL',
        type: 'String',
        description:
          'Website URL of the organization (e.g., “www.company.com”). Used for validation, enrichment, or contact verification.',
        isPK: false,
        isRequired: false,
        isMapped: true,
      },
      {
        name: 'MainPhone',
        type: 'String',
        description: 'Primary contact phone number for the organization’s main office or headquarters.',
        isPK: false,
        isRequired: false,
        isMapped: true,
      },
      {
        name: 'PrimaryAddressID',
        type: 'Foreign Key',
        description: 'Link to the primary address record of the organization (Foreign Key to Address entity).',
        isPK: false,
        isRequired: false,
        isMapped: true,
      },
    ],

    relationships: [
      {
        from: 'Organization',
        relationship: 'hasOne',
        to: 'Address',
        description: 'Organization has one primary address linked via PrimaryAddressID.',
      },
      {
        from: 'Organization',
        relationship: 'hasMany',
        to: 'Person',
        description: 'Organization may have multiple persons (employees, contacts) associated with it.',
      },
      {
        from: 'Organization',
        relationship: 'belongsTo',
        to: 'Party',
        description: 'Organization corresponds to one Party entity (of type Organization).',
      },
    ],

    survivorship: [
      {
        id: 1,
        attribute: 'OrganizationName',
        strategy: 'Most Recent Update',
        sourcePriority: 'CRM > ERP > D&B',
        fallback: 'Latest Non-null Value',
      },
      {
        id: 2,
        attribute: 'WebsiteURL',
        strategy: 'Most Recent Update',
        sourcePriority: 'CRM > Marketing',
        fallback: 'Latest Non-null Value',
      },
      {
        id: 3,
        attribute: 'MainPhone',
        strategy: 'Most Frequent Value',
        sourcePriority: 'CRM > ERP',
        fallback: 'Non-null',
      },
    ],

    cleanse: [
      {
        id: 1,
        ruleName: 'Trim and Normalize Text',
        appliesTo: 'OrganizationName, AlternateName, Industry',
        providerType: 'Built-in',
        order: 1,
        status: 'Active',
      },
      {
        id: 2,
        ruleName: 'Standardize Name Casing',
        appliesTo: 'OrganizationName, AlternateName',
        providerType: 'Built-in (Name Normalization)',
        order: 2,
        status: 'Active',
      },
      {
        id: 3,
        ruleName: 'Validate Website URL',
        appliesTo: 'WebsiteURL',
        providerType: 'Built-in (Regex + Domain Check)',
        order: 3,
        status: 'Active',
      },
      {
        id: 4,
        ruleName: 'Standardize Phone Number Format',
        appliesTo: 'MainPhone',
        providerType: 'Built-in (E.164 Format)',
        order: 4,
        status: 'Active',
      },
      {
        id: 5,
        ruleName: 'Normalize Industry Values',
        appliesTo: 'Industry',
        providerType: 'External API (Industry Standardization)',
        order: 5,
        status: 'Limited',
      },
      {
        id: 6,
        ruleName: 'Validate Tax ID Format',
        appliesTo: 'TaxID',
        providerType: 'Custom Rule (Country-specific Validation)',
        order: 6,
        status: 'Active',
      },
      {
        id: 7,
        ruleName: 'Mask Sensitive Tax Identifiers',
        appliesTo: 'TaxID',
        providerType: 'Built-in (PII Masking)',
        order: 7,
        status: 'Optional',
      },
    ],

    matchRules: [
      {
        label: 'Exact(OrganizationName, TaxID)',
        name: 'OrganizationMatchRule1',
        type: 'Auto Merge',
        scope: 'All',
      },
      {
        label: 'Fuzzy(OrganizationName, Industry)',
        name: 'OrganizationMatchRule2',
        type: 'Potential Match',
        scope: 'All',
      },
    ],
  },
  Person: {
    attributes: [
      {
        name: 'PersonID',
        type: 'UUID',
        description:
          'Unique identifier for the person (often the same value as PartyID for individuals, or a foreign key reference to Party). This links the Person to the Party entity.',
        isPK: true,
        isRequired: true,
        isMapped: true,
      },
      {
        name: 'Title',
        type: 'String',
        description: 'Honorific or salutation (e.g., Mr., Ms., Dr.).',
        isPK: false,
        isRequired: false,
        isMapped: false,
      },
      {
        name: 'FirstName',
        type: 'String',
        description: 'First name (given name) of the person.',
        isPK: false,
        isRequired: true,
        isMapped: true,
      },
      {
        name: 'MiddleName',
        type: 'String',
        description: 'Middle name or initial of the person.',
        isPK: false,
        isRequired: false,
        isMapped: false,
      },
      {
        name: 'LastName',
        type: 'String',
        description: 'Last name (family name or surname) of the person.',
        isPK: false,
        isRequired: true,
        isMapped: true,
      },
      {
        name: 'Suffix',
        type: 'String',
        description: 'Name suffix, if any (e.g., Jr., Sr., III).',
        isPK: false,
        isRequired: false,
        isMapped: false,
      },
      {
        name: 'FullName',
        type: 'String',
        description: 'Computed full name generated by concatenating first, middle, and last names.',
        isPK: false,
        isRequired: false,
        isMapped: false,
      },
      {
        name: 'DateOfBirth',
        type: 'Date',
        description: 'Date of birth of the individual.',
        isPK: false,
        isRequired: false,
        isMapped: false,
      },
      {
        name: 'Gender',
        type: 'String',
        description: 'Gender identifier (e.g., Male, Female, Non-Binary, Prefer not to say).',
        isPK: false,
        isRequired: false,
        isMapped: true,
      },
      {
        name: 'Email',
        type: 'String',
        description: 'Primary email address used for contact or login purposes.',
        isPK: false,
        isRequired: false,
        isMapped: true,
      },
      {
        name: 'Phone',
        type: 'String',
        description: 'Primary phone number for communication.',
        isPK: false,
        isRequired: false,
        isMapped: true,
      },
      {
        name: 'AlternateContact',
        type: 'String',
        description: 'Alternate contact method such as secondary phone number or email address.',
        isPK: false,
        isRequired: false,
        isMapped: false,
      },
      {
        name: 'NationalID',
        type: 'String',
        description: 'Government-issued national identification number (e.g., SSN, Aadhaar, NIN).',
        isPK: false,
        isRequired: false,
        isMapped: false,
      },
      {
        name: 'AddressID',
        type: 'Foreign Key',
        description: 'Link to the primary Address entity associated with this person.',
        isPK: false,
        isRequired: false,
        isMapped: true,
      },
      {
        name: 'Marital_Status',
        type: 'String',
        description: "The individual's current marital status (e.g., Single, Married, Divorced, Widowed).",
        isPK: false,
        isRequired: false,
        isMapped: false,
      },
      {
        name: 'Nationality',
        type: 'String',
        description: "The individual's primary nationality or citizenship.",
        isPK: false,
        isRequired: false,
        isMapped: false,
      },
      {
        name: 'Language_Preference',
        type: 'String',
        description: "The individual's preferred language for communication (e.g., English, Hindi, French).",
        isPK: false,
        isRequired: false,
        isMapped: false,
      },
      {
        name: 'Personal_Email',
        type: 'String',
        description: 'Preferred personal email address used for non-business communication.',
        isPK: false,
        isRequired: false,
        isMapped: true,
      },
      {
        name: 'Business_Email',
        type: 'String',
        description: 'Preferred business or work email address.',
        isPK: false,
        isRequired: false,
        isMapped: true,
      },
      {
        name: 'Passport_Number',
        type: 'String',
        description: 'Passport number of the individual, used for identity verification or international travel.',
        isPK: false,
        isRequired: false,
        isMapped: false,
      },
    ],
    relationships: [
      { from: 'Person', relationship: 'belongsTo', to: 'Organization' },
      { from: 'Person', relationship: 'hasOne', to: 'Address' },
    ],

    survivorship: [
      {
        id: 1,
        attribute: 'FirstName',
        strategy: 'Preferred Source',
        sourcePriority: 'CRM > HR > ERP',
        fallback: 'Latest Non-null Value',
      },
      {
        id: 2,
        attribute: 'LastName',
        strategy: 'Preferred Source',
        sourcePriority: 'CRM > HR > ERP',
        fallback: 'Latest Non-null Value',
      },
      {
        id: 3,
        attribute: 'Email',
        strategy: 'Most Recent Update',
        sourcePriority: 'CRM > HR',
        fallback: 'Non-null',
      },
      {
        id: 4,
        attribute: 'Phone',
        strategy: 'Highest Data Quality Score',
        sourcePriority: 'CRM > HR',
        fallback: 'Non-null',
      },
      {
        id: 5,
        attribute: 'Gender',
        strategy: 'Most Frequent Value',
        sourcePriority: 'CRM > HR',
        fallback: 'Non-null',
      },
      {
        id: 6,
        attribute: 'Nationality',
        strategy: 'Preferred Source',
        sourcePriority: 'HR > Passport Authority',
        fallback: 'Latest Non-null Value',
      },
      {
        id: 7,
        attribute: 'Language_Preference',
        strategy: 'Most Frequent Value',
        sourcePriority: 'CRM > HR',
        fallback: 'English by Default',
      },
    ],

    cleanse: [
      {
        id: 1,
        ruleName: 'Trim and Normalize Text',
        appliesTo: 'All text attributes (Names, Emails, Gender, Nationality)',
        providerType: 'Built-in',
        order: 1,
        status: 'Active',
      },
      {
        id: 2,
        ruleName: 'Standardize Name Casing',
        appliesTo: 'FirstName, LastName, MiddleName',
        providerType: 'Built-in (Name Normalization)',
        order: 2,
        status: 'Active',
      },
      {
        id: 3,
        ruleName: 'Validate Email Formats',
        appliesTo: 'Email, Personal_Email, Business_Email',
        providerType: 'Built-in (Regex + Domain Check)',
        order: 3,
        status: 'Active',
      },
      {
        id: 4,
        ruleName: 'Standardize Phone Number Format',
        appliesTo: 'Phone, AlternateContact',
        providerType: 'Built-in (E.164 Format)',
        order: 4,
        status: 'Active',
      },
      {
        id: 5,
        ruleName: 'Validate Gender and Marital Status',
        appliesTo: 'Gender, Marital_Status',
        providerType: 'Custom Rule (Enum Validation)',
        order: 5,
        status: 'Active',
      },
      {
        id: 6,
        ruleName: 'Normalize Nationality Values',
        appliesTo: 'Nationality',
        providerType: 'External API (Country Code Validation)',
        order: 6,
        status: 'Limited',
      },
      {
        id: 7,
        ruleName: 'Mask PII for Non-Production',
        appliesTo: 'NationalID, Passport_Number, Email',
        providerType: 'Built-in (PII Masking)',
        order: 7,
        status: 'Optional',
      },
      {
        id: 8,
        ruleName: 'Enrich Missing Personal Info',
        appliesTo: 'Nationality, Language_Preference',
        providerType: 'External API (HR/CRM Enrichment)',
        order: 8,
        status: 'Optional',
      },
    ],

    matchRules: [
      {
        label: 'Exact(FirstName, LastName, DateOfBirth)',
        name: 'PersonMatchRule1',
        type: 'Potential Match',
        scope: 'All',
      },
      { label: 'Fuzzy(Name, Email)', name: 'PersonMatchRule2', type: 'Potential Match', scope: 'All' },
      { label: 'Exact(NationalID)', name: 'PersonMatchRule3', type: 'Auto Merge', scope: 'All' },
      { label: 'Exact(Passport_Number)', name: 'PersonMatchRule4', type: 'Auto Merge', scope: 'All' },
    ],
  },
  Address: {
    attributes: [
      {
        name: 'AddressID',
        type: 'String',
        description: 'Unique identifier for the address record.',
        isPK: true,
        isRequired: true,
        isMapped: true,
      },
      {
        name: 'PartyID',
        type: 'Foreign Key',
        description: 'Reference to the Party (Person or Organization) that this address belongs to.',
        isPK: false,
        isRequired: true,
        isMapped: true,
      },
      {
        name: 'AddressType',
        type: 'String',
        description: 'Type of address, e.g., Home, Work, Billing, Shipping, or Mailing.',
        isPK: false,
        isRequired: true,
        isMapped: true,
      },
      {
        name: 'Line1',
        type: 'String',
        description: 'Primary address line (e.g., "123 Main Street").',
        isPK: false,
        isRequired: true,
        isMapped: true,
      },
      {
        name: 'Line2',
        type: 'String',
        description: 'Additional address information like apartment, suite, or PO Box (e.g., "Apt 4B").',
        isPK: false,
        isRequired: false,
        isMapped: false,
      },
      {
        name: 'City',
        type: 'String',
        description: 'City name.',
        isPK: false,
        isRequired: true,
        isMapped: true,
      },
      {
        name: 'StateProvince',
        type: 'String',
        description: 'State or province name.',
        isPK: false,
        isRequired: true,
        isMapped: true,
      },
      {
        name: 'PostalCode',
        type: 'String',
        description: 'ZIP or postal code.',
        isPK: false,
        isRequired: true,
        isMapped: true,
      },
      {
        name: 'Country',
        type: 'String',
        description: 'Country name or ISO country code.',
        isPK: false,
        isRequired: true,
        isMapped: true,
      },
      {
        name: 'Latitude',
        type: 'Decimal',
        description: 'Geographic latitude (optional, if geocoding is done).',
        isPK: false,
        isRequired: false,
        isMapped: false,
      },
      {
        name: 'Longitude',
        type: 'Decimal',
        description: 'Geographic longitude (optional).',
        isPK: false,
        isRequired: false,
        isMapped: false,
      },
      {
        name: 'ValidFrom',
        type: 'Date',
        description: 'Date when this address became effective.',
        isPK: false,
        isRequired: false,
        isMapped: false,
      },
      {
        name: 'ValidTo',
        type: 'Date',
        description: 'Date when this address expired or became inactive.',
        isPK: false,
        isRequired: false,
        isMapped: false,
      },
      {
        name: 'IsPrimary',
        type: 'Boolean',
        description: 'Indicates if this is the primary address for the Party.',
        isPK: false,
        isRequired: false,
        isMapped: true,
      },
    ],

    relationships: [
      {
        from: 'Address',
        relationship: 'belongsTo',
        to: 'Party',
        description: 'Each Address record is associated with one Party (either Person or Organization).',
      },
    ],

    survivorship: [
      {
        id: 1,
        attribute: 'Line1',
        strategy: 'Most Recent Update',
        sourcePriority: 'CRM > ERP > HR',
        fallback: 'Latest Non-null Value',
      },
      {
        id: 2,
        attribute: 'City',
        strategy: 'Most Frequent Value',
        sourcePriority: 'CRM > ERP',
        fallback: 'Most Common Value',
      },
      {
        id: 3,
        attribute: 'PostalCode',
        strategy: 'Most Frequent Value',
        sourcePriority: 'CRM > ERP',
        fallback: 'Most Common Value',
      },
    ],

    cleanse: [
      {
        id: 1,
        ruleName: 'Trim and Normalize Text',
        appliesTo: 'All text attributes (Line1, Line2, City, StateProvince, Country)',
        providerType: 'Built-in',
        order: 1,
        status: 'Active',
      },
      {
        id: 2,
        ruleName: 'Standardize Country Codes',
        appliesTo: 'Country',
        providerType: 'External API (ISO Validator)',
        order: 2,
        status: 'Active',
      },
      {
        id: 3,
        ruleName: 'Format Postal Code',
        appliesTo: 'PostalCode',
        providerType: 'Built-in (Regex Validation)',
        order: 3,
        status: 'Active',
      },
      {
        id: 4,
        ruleName: 'Geocode Validation',
        appliesTo: 'Latitude, Longitude',
        providerType: 'External Geocoding Service',
        order: 4,
        status: 'Optional',
      },
    ],

    matchRules: [
      {
        label: 'Exact(Line1, PostalCode, Country)',
        name: 'AddressMatchRule1',
        type: 'Potential Match',
        scope: 'All',
      },
      {
        label: 'Fuzzy(City, StateProvince, Country)',
        name: 'AddressMatchRule2',
        type: 'Potential Match',
        scope: 'All',
      },
    ],
  },
};
